import React, { useRef, useState, useEffect, useMemo } from 'react';
import './PanoDetayPanel.css';
import { usePanels } from '../../../context/PanelsContext';
import { generatePanoDetayV2, clearPanoDetay } from '../../../utils/generatePanoDetayV2';
import { useSelector, useDispatch } from 'react-redux';
import { selectPanelDetailDefaults } from '../../../redux/panelDetailSlice';
import { setPanoDetayConfig, setEnergyRoomLaunchPayload } from '../../../redux/energyRoomSlice';
import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { expandSimpleSupplyForFloorCopies } from '../../../utils/simpleTransformFunctions';
import { selectSortedFloorPlans } from '../../../redux/floorPlanSlice';

const PanoDetayPanel = ({ scene }) => {
  const dispatch = useDispatch();
  const panelDetailDefaults = useSelector(selectPanelDetailDefaults);
  const simpleSupply = useSelector((state) => state.simpleSupply);
  const floorPlans = useSelector(selectSortedFloorPlans);

  // Sayaç adetlerini projeden hesapla (kat kopyalarını dahil et)
  const { derived3F, derived1F } = useMemo(() => {
    const expanded = expandSimpleSupplyForFloorCopies(simpleSupply, floorPlans);
    const comps = expanded?.components;
    if (!comps || !Array.isArray(comps)) return { derived3F: 0, derived1F: 0 };
    let c3 = 0, c1 = 0;
    for (const c of comps) {
      if (c.kind !== COMPONENT_KINDS.DIST_PANEL && c.kind !== COMPONENT_KINDS.PANEL) continue;
      const phase = c.electrical?.phaseCount;
      if (phase === 3) c3++;
      else c1++;
    }
    return { derived3F: c3, derived1F: c1 };
  }, [simpleSupply, floorPlans]);

  // Sayaç adetleri
  const [panoTopology, setPanoTopology] = useState('A');
  const [pano1FazAdet, setPano1FazAdet] = useState('4');
  const [pano3FazAdet, setPano3FazAdet] = useState('0');

  // Proje verisi değiştiğinde sayaç adetlerini güncelle
  useEffect(() => {
    if (derived3F > 0 || derived1F > 0) {
      setPano3FazAdet(String(derived3F));
      setPano1FazAdet(String(derived1F));
    }
  }, [derived3F, derived1F]);
  // Sayaç bölmesi ayarları
  const [panoMaxRows3, setPanoMaxRows3] = useState('3');
  const [panoMaxRows1, setPanoMaxRows1] = useState('4');
  const [panoMaxHeight, setPanoMaxHeight] = useState('150');
  // Biçim ayarları
  const [panoDimTextSize, setPanoDimTextSize] = useState('11');
  const [panoDimOffset, setPanoDimOffset] = useState('5');
  const [panoDimArrowSize, setPanoDimArrowSize] = useState('3');
  const [panoDimGap, setPanoDimGap] = useState('8');
  // Kapak ayarları
  const [panoMaxDoorWidth, setPanoMaxDoorWidth] = useState('60');
  // Bara genişlik modu
  const [panoBaraGenislik, setPanoBaraGenislik] = useState('BOTH');
  // Sayaç yerleşim modu
  const [panoPlacementMode, setPanoPlacementMode] = useState('row');
  // Tek fazları kaydır
  const [panoShiftSinglePhase, setPanoShiftSinglePhase] = useState(false);
  // Ana Kesici (TMS) ayarlari
  const [tmsAmperaj, setTmsAmperaj] = useState('100');
  const [tmsFaz, setTmsFaz] = useState('4');
  // AOS sigorta adetleri
  const [aos4PAdet, setAos4PAdet] = useState('0');
  const [aos2PAdet, setAos2PAdet] = useState('0');
  // Pano bölme ayarları
  const [dagitimAyir, setDagitimAyir] = useState(false);
  const [bolmeSayisi, setBolmeSayisi] = useState(1);
  const [bolmeSutunlar, setBolmeSutunlar] = useState([]);
  // Kapaklı görünüm alt/sağ
  const [kapakliAltinda, setKapakliAltinda] = useState(false);

  // Toplam sütun sayısı hesaplama
  const totalCols = useMemo(() => {
    const count3F = parseInt(pano3FazAdet) || 0;
    const count1F = parseInt(pano1FazAdet) || 0;
    const maxRows3 = parseInt(panoMaxRows3) || 3;
    const maxRows1 = parseInt(panoMaxRows1) || 4;

    if (panoPlacementMode === 'column') {
      const cols3 = count3F > 0 ? Math.ceil(count3F / maxRows3) : 0;
      const cols1 = count1F > 0 ? Math.ceil(count1F / maxRows1) : 0;
      return cols3 + cols1;
    } else {
      if (count3F > 0) {
        return Math.ceil(count3F / maxRows3);
      } else {
        return count1F > 0 ? Math.ceil(count1F / maxRows1) : 0;
      }
    }
  }, [pano3FazAdet, pano1FazAdet, panoMaxRows3, panoMaxRows1, panoPlacementMode]);

  // bolmeSayisi veya totalCols değişince bolmeSutunlar'ı güncelle
  useEffect(() => {
    if (bolmeSayisi <= 1 || totalCols < 2) {
      setBolmeSutunlar([]);
      return;
    }
    const base = Math.floor(totalCols / bolmeSayisi);
    const remainder = totalCols - base * (bolmeSayisi - 1);
    const arr = Array(bolmeSayisi - 1).fill(base);
    arr.push(remainder);
    setBolmeSutunlar(arr);
  }, [bolmeSayisi, totalCols]);

  // Tek bir bölmenin sütun sayısını değiştirme
  const handleBolmeSutunChange = (idx, value) => {
    setBolmeSutunlar(prev => {
      const next = [...prev];
      next[idx] = parseInt(value) || 1;
      // idx'ten sonraki tüm parçaları yeniden dağıt
      const sumBefore = next.slice(0, idx + 1).reduce((a, b) => a + b, 0);
      const remainingCols = totalCols - sumBefore;
      const remainingParts = next.length - idx - 1;
      if (remainingParts > 0) {
        const base = Math.floor(remainingCols / remainingParts);
        const extra = remainingCols - base * remainingParts;
        for (let i = idx + 1; i < next.length; i++) {
          next[i] = i === idx + 1 ? base + extra : base;
        }
      }
      return next;
    });
  };

  // Pano detay çizimi
  const handlePanoDetayOlustur = () => {
    if (!scene) {
      console.warn('[PanoDetayPanel] Scene objesi yok');
      return;
    }
    const count3F = parseInt(pano3FazAdet) || 0;
    const count1F = parseInt(pano1FazAdet) || 0;
    const meters = [];
    for (let i = 0; i < count3F; i++) {
      meters.push({ id: `3F-${i}`, phase: 3, label: `3F-${i + 1}` });
    }
    for (let i = 0; i < count1F; i++) {
      meters.push({ id: `1F-${i}`, phase: 1, label: `1F-${i + 1}` });
    }
    if (meters.length === 0) {
      console.warn('[PanoDetayPanel] Sayaç adedi 0, çizim yapılamaz');
      return;
    }
    const derivedDistHeightMode = panoBaraGenislik === 'METER_ONLY'
      ? 'METER_PLUS_BUSBAR'
      : 'METER_ONLY';

    const mergedConfig = {
      ...panelDetailDefaults,
      meter: {
        ...panelDetailDefaults.meter,
        phase3: {
          ...panelDetailDefaults.meter.phase3,
          maxRows: parseInt(panoMaxRows3) || 3,
        },
        phase1: {
          ...panelDetailDefaults.meter.phase1,
          maxRows: parseInt(panoMaxRows1) || 4,
        },
      },
      meterCompartment: {
        ...panelDetailDefaults.meterCompartment,
        maxHeight: parseInt(panoMaxHeight) || 150,
        maxDoorWidth: parseInt(panoMaxDoorWidth) || 60,
        placementMode: panoPlacementMode,
        shiftSinglePhase: panoShiftSinglePhase,
      },
      busbarCompartment: {
        ...panelDetailDefaults.busbarCompartment,
        widthMode: panoBaraGenislik,
      },
      distributionCompartment: {
        ...panelDetailDefaults.distributionCompartment,
        heightMode: derivedDistHeightMode,
        mainBreaker: {
          current: parseInt(tmsAmperaj) || 100,
          poles: parseInt(tmsFaz) || 4,
        },
        aos: {
          breakers: [
            ...(parseInt(aos4PAdet) > 0 ? [{ phase: 3, count: parseInt(aos4PAdet) }] : []),
            ...(parseInt(aos2PAdet) > 0 ? [{ phase: 1, count: parseInt(aos2PAdet) }] : []),
          ],
        },
      },
      dimensions: {
        ...panelDetailDefaults.dimensions,
        textSize: parseInt(panoDimTextSize) || 11,
        offset: parseInt(panoDimOffset) || 5,
        arrowSize: parseInt(panoDimArrowSize) || 3,
        gap: parseInt(panoDimGap) || 8,
      },
    };
    const panoConfig = {
      meters,
      topology: panoTopology,
      panelName: 'Sayaç Panosu',
      config: mergedConfig,
      splitColumns: bolmeSayisi >= 2 ? bolmeSutunlar.join('/') : '',
      separateDistribution: dagitimAyir,
      doorViewBelow: kapakliAltinda,
    };
    dispatch(setPanoDetayConfig(panoConfig));
    generatePanoDetayV2(scene, panoConfig, { centerX: 0, centerY: -2000 });
  };

  // ─── Enerji Odası açılış payload'u ───
  // Enerji Odası butonu Tesisat menüsüne taşındı. Bu panel güncel ayarlardan
  // payload'u türetip redux'a sync eder; Tesisat butonu oradan okuyup açar.
  const energyRoomLaunchPayload = useMemo(() => {
    const count3F = parseInt(pano3FazAdet) || 0;
    const count1F = parseInt(pano1FazAdet) || 0;
    const meters = [];
    for (let i = 0; i < count3F; i++) meters.push({ id: `3F-${i}`, phase: 3, label: `3F-${i + 1}` });
    for (let i = 0; i < count1F; i++) meters.push({ id: `1F-${i}`, phase: 1, label: `1F-${i + 1}` });
    const derivedDistHeightMode = panoBaraGenislik === 'METER_ONLY' ? 'METER_PLUS_BUSBAR' : 'METER_ONLY';
    const mergedConfig = {
      ...panelDetailDefaults,
      meter: { ...panelDetailDefaults.meter,
        phase3: { ...panelDetailDefaults.meter.phase3, maxRows: parseInt(panoMaxRows3) || 3 },
        phase1: { ...panelDetailDefaults.meter.phase1, maxRows: parseInt(panoMaxRows1) || 4 },
      },
      meterCompartment: { ...panelDetailDefaults.meterCompartment,
        maxHeight: parseInt(panoMaxHeight) || 150, maxDoorWidth: parseInt(panoMaxDoorWidth) || 60,
        placementMode: panoPlacementMode, shiftSinglePhase: panoShiftSinglePhase,
      },
      busbarCompartment: { ...panelDetailDefaults.busbarCompartment, widthMode: panoBaraGenislik },
      distributionCompartment: { ...panelDetailDefaults.distributionCompartment,
        heightMode: derivedDistHeightMode,
        mainBreaker: { current: parseInt(tmsAmperaj) || 100, poles: parseInt(tmsFaz) || 4 },
        aos: { breakers: [
          ...(parseInt(aos4PAdet) > 0 ? [{ phase: 3, count: parseInt(aos4PAdet) }] : []),
          ...(parseInt(aos2PAdet) > 0 ? [{ phase: 1, count: parseInt(aos2PAdet) }] : []),
        ]},
      },
      dimensions: { ...panelDetailDefaults.dimensions,
        textSize: parseInt(panoDimTextSize) || 11, offset: parseInt(panoDimOffset) || 5,
        arrowSize: parseInt(panoDimArrowSize) || 3, gap: parseInt(panoDimGap) || 8,
      },
    };
    return { config: mergedConfig, meters, separateDistribution: dagitimAyir, splitParts: bolmeSayisi >= 2 ? bolmeSutunlar : [] };
  }, [pano3FazAdet, pano1FazAdet, panoMaxRows3, panoMaxRows1, panoMaxHeight, panoMaxDoorWidth, panoPlacementMode, panoShiftSinglePhase, panoBaraGenislik, tmsAmperaj, tmsFaz, aos4PAdet, aos2PAdet, panoDimTextSize, panoDimOffset, panoDimArrowSize, panoDimGap, dagitimAyir, bolmeSayisi, bolmeSutunlar, panelDetailDefaults]);

  useEffect(() => {
    dispatch(setEnergyRoomLaunchPayload(energyRoomLaunchPayload));
  }, [energyRoomLaunchPayload, dispatch]);

  // ─── Panel sürükleme / boyutlandırma ───
  const ref = useRef(null);
  const headerRef = useRef(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });

  const [collapsed, setCollapsed] = useState(true);
  const [panelHeight, setPanelHeight] = useState(400);

  const TOPBAR_HEIGHT = 115;
  const SNAP_THRESHOLD = 30;

  const { registerPanel, updatePanelPosition, panels, setPanelHeight: setStackPanelHeight, getStackTop, activeTabId } = usePanels();
  const wasDragged = useRef(false);

  const toggle = () => setCollapsed(prev => !prev);

  const showSnapHighlight = () => {
    const el = ref.current;
    if (!el) return;
    el.classList.add('snap-highlight');
    setTimeout(() => el.classList.remove('snap-highlight'), 300);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    registerPanel("PanoDetayPanel", {
      top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
    });
  }, []);

  // Collapse/expand değişince boyut güncelle + stack height bildir
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      updatePanelPosition("PanoDetayPanel", {
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height, collapsed,
      });
      setStackPanelHeight("PanoDetayPanel", collapsed ? 40 : rect.height);
    });
  }, [collapsed]);

  // RESTORE — yalnız PAFTA değişince: konum + açık/kapalı geri yükle. getStackTop'a
  // bağlı DEĞİL → açma/kapama bu effect'i tetiklemez.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stored = panels["PanoDetayPanel"];
    if (stored?.dragged) {
      el.style.left = `${stored.left}px`;
      el.style.top = `${stored.top}px`;
      wasDragged.current = true;
    } else {
      wasDragged.current = false;
      const stackTop = getStackTop("PanoDetayPanel");
      el.style.left = '';
      el.style.top = `${stackTop}px`;
      el.style.maxHeight = `${window.innerHeight - stackTop}px`;
    }
    const wantCollapsed = stored && typeof stored.collapsed === 'boolean' ? stored.collapsed : true;
    if (wantCollapsed !== collapsed) setCollapsed(wantCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Pafta-İÇİ yeniden stack (sürüklenmemiş paneller)
  useEffect(() => {
    const el = ref.current;
    if (!el || wasDragged.current) return;
    const stackTop = getStackTop("PanoDetayPanel");
    el.style.top = `${stackTop}px`;
    el.style.maxHeight = `${window.innerHeight - stackTop}px`;
  }, [getStackTop]);

  useEffect(() => {
    const el = ref.current;
    const header = headerRef.current;

    const onMouseDown = (e) => {
      if (isResizing.current) return;
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - el.offsetLeft,
        y: e.clientY - el.offsetTop,
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      let x = e.clientX - dragOffset.current.x;
      let y = e.clientY - dragOffset.current.y;
      const ww = window.innerWidth;
      const w = el.offsetWidth;

      if (x < SNAP_THRESHOLD) { x = 0; showSnapHighlight(); }
      if (x + w > ww - SNAP_THRESHOLD) { x = ww - w; showSnapHighlight(); }
      if (y < TOPBAR_HEIGHT + SNAP_THRESHOLD) { y = TOPBAR_HEIGHT; showSnapHighlight(); }

      const h = el.offsetHeight;
      Object.entries(panels).forEach(([key, p]) => {
        if (key === "PanoDetayPanel") return;
        if (Math.abs(x - (p.left + p.width)) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left + p.width; y = p.top; showSnapHighlight(); }
        if (Math.abs(x + w - p.left) < SNAP_THRESHOLD && Math.abs(y - p.top) < p.height) { x = p.left - w; y = p.top; showSnapHighlight(); }
        if (Math.abs(y - (p.top + p.height)) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top + p.height; x = p.left; showSnapHighlight(); }
        if (Math.abs(y + h - p.top) < SNAP_THRESHOLD && Math.abs(x - p.left) < p.width) { y = p.top - h; x = p.left; showSnapHighlight(); }
      });

      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.maxHeight = `${window.innerHeight - y}px`;
      wasDragged.current = true;
      updatePanelPosition("PanoDetayPanel", { top: y, left: x, dragged: true });
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
    return () => header.removeEventListener('mousedown', onMouseDown);
  }, [panels]);

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    const rect = ref.current.getBoundingClientRect();
    startSize.current = { width: rect.width, height: rect.height < panelHeight ? rect.height : panelHeight };
    document.addEventListener('mousemove', handleResizing);
    document.addEventListener('mouseup', stopResizing);
  };

  const handleResizing = (e) => {
    if (!isResizing.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    const newWidth = startSize.current.width + dx;
    const newHeight = startSize.current.height + dy;
    if (newWidth > 200) ref.current.style.width = `${newWidth}px`;
    if (newHeight < panelHeight) ref.current.style.height = `${newHeight}px`;
  };

  const stopResizing = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResizing);
    document.removeEventListener('mouseup', stopResizing);
  };

  return (
    <div
      ref={ref}
      className={`PanoDetayPanel ${collapsed ? 'collapsed' : ''}`}
      style={{ height: collapsed ? '40px' : undefined }}
    >
      <div className="PanoDetayPanel-header" ref={headerRef}>
        <span onClick={toggle}>Pano Detay Paneli</span>
        <span style={{ fontSize: '18px', cursor: 'pointer' }} onClick={toggle}>
          {collapsed ? '▼' : '▲'}
        </span>
      </div>
      <div className="PanoDetayPanel-content">
        {/* Sayaç Adetleri */}
        <table className="info-table-grid">
          <tbody>
            <tr>
              <td className="info-label">3 Faz Sayaç Adedi</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={pano3FazAdet} min="0" max="100"
                  onChange={(e) => setPano3FazAdet(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">1 Faz Sayaç Adedi</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={pano1FazAdet} min="0" max="100"
                  onChange={(e) => setPano1FazAdet(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Toplam Sayaç</td>
              <td className="info-value">{(parseInt(pano3FazAdet) || 0) + (parseInt(pano1FazAdet) || 0)}</td>
            </tr>
            <tr>
              <td className="info-label">Yerleşim Tipi</td>
              <td className="info-value">
                <select className="info-select"
                  value={panoTopology}
                  onChange={(e) => setPanoTopology(e.target.value)}>
                  <option value="A">A - Standart</option>
                  <option value="B">B - Yan Yana</option>
                  <option value="C">C - Dikey Bölme</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
        {/* Bölme Ayarları */}
        <div style={{ fontSize: 11, color: '#4fc3f7', padding: '6px 4px 2px', fontWeight: 'bold' }}>Bölme Ayarları</div>
        <table className="info-table-grid">
          <tbody>
            <tr>
              <td className="info-label">Yerleşim Modu</td>
              <td className="info-value">
                <select className="info-select"
                  value={panoPlacementMode}
                  onChange={(e) => setPanoPlacementMode(e.target.value)}>
                  <option value="row">Satır</option>
                  <option value="column">Sütun</option>
                </select>
              </td>
            </tr>
            <tr>
              <td className="info-label">Tek Fazları Kaydır</td>
              <td className="info-value">
                <input type="checkbox" className="info-checkbox"
                  checked={panoShiftSinglePhase}
                  onChange={() => setPanoShiftSinglePhase(prev => !prev)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Dağıtımı Ayır</td>
              <td className="info-value">
                <input type="checkbox" className="info-checkbox"
                  checked={dagitimAyir}
                  onChange={() => setDagitimAyir(prev => !prev)} />
              </td>
            </tr>
            {totalCols >= 2 && (
            <tr>
              <td className="info-label">Sayaç Bölme</td>
              <td className="info-value">
                <select className="info-select"
                  value={bolmeSayisi}
                  onChange={(e) => setBolmeSayisi(parseInt(e.target.value) || 1)}>
                  {Array.from({ length: totalCols }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}{i === 0 ? ' (Bölme Yok)' : ' parça'}</option>
                  ))}
                </select>
              </td>
            </tr>
            )}
            {bolmeSutunlar.map((val, idx) => {
              const sumBefore = bolmeSutunlar.slice(0, idx).reduce((a, b) => a + b, 0);
              const remaining = totalCols - sumBefore;
              const partsAfter = bolmeSutunlar.length - 1 - idx;
              const maxForThis = remaining - partsAfter;
              return (
              <tr key={idx}>
                <td className="info-label">Bölme {idx + 1} Sütun</td>
                <td className="info-value">
                  {idx < bolmeSutunlar.length - 1 ? (
                    <select className="info-select"
                      value={val}
                      onChange={(e) => handleBolmeSutunChange(idx, e.target.value)}>
                      {Array.from({ length: Math.max(1, maxForThis) }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ padding: '0 4px', color: '#aaa' }}>{val} (otomatik)</span>
                  )}
                </td>
              </tr>
              );
            })}
            <tr>
              <td className="info-label">Kapaklı Alta</td>
              <td className="info-value">
                <input type="checkbox" className="info-checkbox"
                  checked={kapakliAltinda}
                  onChange={() => setKapakliAltinda(prev => !prev)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Bara Genişliği</td>
              <td className="info-value">
                <select className="info-select"
                  value={panoBaraGenislik}
                  onChange={(e) => setPanoBaraGenislik(e.target.value)}>
                  <option value="BOTH">Sayaç + Dağıtım</option>
                  <option value="METER_ONLY">Sadece Sayaç</option>
                </select>
              </td>
            </tr>
            <tr>
              <td className="info-label">3F Max Satır</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoMaxRows3} min="1" max="10"
                  onChange={(e) => setPanoMaxRows3(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">1F Max Satır</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoMaxRows1} min="1" max="10"
                  onChange={(e) => setPanoMaxRows1(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Max Yükseklik (cm)</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoMaxHeight} min="50" max="300"
                  onChange={(e) => setPanoMaxHeight(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Max Kapak Genişliği (cm)</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoMaxDoorWidth} min="20" max="120"
                  onChange={(e) => setPanoMaxDoorWidth(e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>
        {/* Ana Kesici (TMS) Ayarları */}
        <div style={{ fontSize: 11, color: '#4fc3f7', padding: '6px 4px 2px', fontWeight: 'bold' }}>Ana Kesici (TMS)</div>
        <table className="info-table-grid">
          <tbody>
            <tr>
              <td className="info-label">Amperaj</td>
              <td className="info-value">
                <select className="info-select"
                  value={tmsAmperaj}
                  onChange={(e) => setTmsAmperaj(e.target.value)}>
                  {[16,25,32,40,50,63,80,100,125,160,200,225,250,315,320,400].map(a => (
                    <option key={a} value={String(a)}>{a} A</option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td className="info-label">Faz / Kutup</td>
              <td className="info-value">
                <select className="info-select"
                  value={tmsFaz}
                  onChange={(e) => setTmsFaz(e.target.value)}>
                  <option value="4">4P (3 Faz + N)</option>
                  <option value="3">3P (3 Faz)</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
        {/* AOS Sigortaları */}
        <div style={{ fontSize: 11, color: '#4fc3f7', padding: '6px 4px 2px', fontWeight: 'bold' }}>AOS Sigortaları</div>
        <table className="info-table-grid">
          <tbody>
            <tr>
              <td className="info-label">4 Kutuplu (3F)</td>
              <td className="info-value">
                <select className="info-select"
                  value={aos4PAdet}
                  onChange={(e) => setAos4PAdet(e.target.value)}>
                  {Array.from({ length: 13 }, (_, i) => (
                    <option key={i} value={String(i)}>{i} adet</option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td className="info-label">2 Kutuplu (1F)</td>
              <td className="info-value">
                <select className="info-select"
                  value={aos2PAdet}
                  onChange={(e) => setAos2PAdet(e.target.value)}>
                  {Array.from({ length: 13 }, (_, i) => (
                    <option key={i} value={String(i)}>{i} adet</option>
                  ))}
                </select>
              </td>
            </tr>
          </tbody>
        </table>
        {/* Biçim Ayarları */}
        <div style={{ fontSize: 11, color: '#4fc3f7', padding: '6px 4px 2px', fontWeight: 'bold' }}>Biçim Ayarları</div>
        <table className="info-table-grid">
          <tbody>
            <tr>
              <td className="info-label">Yazı Boyutu</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoDimTextSize} min="4" max="24"
                  onChange={(e) => setPanoDimTextSize(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Ölçü Boşluğu (gap)</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoDimGap} min="0" max="30"
                  onChange={(e) => setPanoDimGap(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Ölçü Aralığı (offset)</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoDimOffset} min="1" max="30"
                  onChange={(e) => setPanoDimOffset(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="info-label">Ok Boyutu</td>
              <td className="info-value">
                <input type="number" className="info-input"
                  value={panoDimArrowSize} min="1" max="10"
                  onChange={(e) => setPanoDimArrowSize(e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>
        {/* Butonlar */}
        <div style={{ display: 'flex', gap: 6, padding: '6px 0' }}>
          <button
            className="accordion-header"
            style={{ flex: 1, fontSize: 12, justifyContent: 'center', borderRadius: 4 }}
            onClick={handlePanoDetayOlustur}
          >
            Pano Detay Oluştur
          </button>
          <button
            className="accordion-header"
            style={{ flex: 1, fontSize: 12, justifyContent: 'center', borderRadius: 4 }}
            onClick={() => scene && clearPanoDetay(scene)}
          >
            Temizle
          </button>
        </div>
      </div>
      <div className="resize-handle" onMouseDown={handleResizeMouseDown} />
    </div>
  );
};

export default PanoDetayPanel;
